import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import whatsappTemplateService, {
  WhatsAppTemplate,
  ButtonComponent,
  TemplateComponents,
  VariableMappingRow,
} from '../../services/whatsappTemplateService';

const SOURCE_KEYS = [
  'guest_name',
  'property_name',
  'reservation_id',
  'crs_no',
  'arrival_date',
  'departure_date',
  'guest_country',
  'guest_state',
  'guest_city',
  'guest_email',
  'check_in_date',
  'check_out_date',
] as const;

const emptyComponents = (): TemplateComponents => ({
  header: { enabled: false, format: 'TEXT', text: '' },
  body: { text: '' },
  footer: { enabled: false, text: '' },
  buttons: [],
});

const WhatsAppTemplateEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [metaTemplateName, setMetaTemplateName] = useState('');
  const [languageCode, setLanguageCode] = useState('en');
  const [templateCategory, setTemplateCategory] = useState<'UTILITY' | 'MARKETING' | 'AUTHENTICATION'>('MARKETING');
  const [parameterFormat, setParameterFormat] = useState<'POSITIONAL' | 'NAMED'>('POSITIONAL');
  const [components, setComponents] = useState<TemplateComponents>(emptyComponents);
  const [variableMapping, setVariableMapping] = useState<VariableMappingRow[]>([]);
  const [status, setStatus] = useState<string>('PENDING');
  const [loading, setLoading] = useState(false);

  const fetchTemplate = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await whatsappTemplateService.getById(Number(id));
      setName(data.name);
      setMetaTemplateName(data.meta_template_name ?? '');
      setLanguageCode(data.language_code ?? 'en');
      setTemplateCategory((data.template_category as typeof templateCategory) || 'MARKETING');
      setParameterFormat((data.parameter_format as 'POSITIONAL' | 'NAMED') || 'POSITIONAL');
      setStatus(data.status);
      if (data.components && typeof data.components === 'object' && (data.components as TemplateComponents).body) {
        setComponents({ ...emptyComponents(), ...data.components, body: { ...data.components.body } });
      } else {
        setComponents({
          ...emptyComponents(),
          body: { text: data.content || '' },
        });
      }
      setVariableMapping(Array.isArray(data.variable_mapping) ? (data.variable_mapping as VariableMappingRow[]) : []);
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      void fetchTemplate();
    }
  }, [id, fetchTemplate]);

  const updateButton = (index: number, field: keyof ButtonComponent, value: string) => {
    setComponents((c: TemplateComponents) => {
      const next: TemplateComponents = { ...c, buttons: [...(c.buttons || [])] };
      const row: ButtonComponent = { ...(next.buttons![index] || { type: 'QUICK_REPLY', text: '' }), [field]: value };
      next.buttons![index] = row;
      return next;
    });
  };

  const addButton = () => {
    setComponents((c: TemplateComponents) => ({
      ...c,
      buttons: [...(c.buttons || []), { type: 'QUICK_REPLY' as const, text: '' }],
    }));
  };

  const removeButton = (index: number) => {
    setComponents((c: TemplateComponents) => ({
      ...c,
      buttons: (c.buttons || []).filter((_b: ButtonComponent, i: number) => i !== index),
    }));
  };

  const autofillMapping = () => {
    const pf = parameterFormat;
    const texts: { section: VariableMappingRow['section']; text: string; buttonIndex?: number }[] = [];
    if (components.header?.enabled && components.header.text) {
      texts.push({ section: 'header', text: components.header.text });
    }
    texts.push({ section: 'body', text: components.body?.text || '' });
    if (components.footer?.enabled && components.footer.text) {
      texts.push({ section: 'footer', text: components.footer.text });
    }
    (components.buttons || []).forEach((b: ButtonComponent, i: number) => {
      if (b.type === 'URL' && b.url) {
        texts.push({ section: 'button', text: b.url, buttonIndex: i });
      }
    });
    const rows: VariableMappingRow[] = [];
    for (const t of texts) {
      if (pf === 'POSITIONAL') {
        const re = /\{\{(\d+)\}\}/g;
        let m: RegExpExecArray | null;
        const seen = new Set<string>();
        while ((m = re.exec(t.text)) !== null) {
          const n = m[1];
          const k = `${t.section}-${n}`;
          if (seen.has(k)) continue;
          seen.add(k);
          const r: VariableMappingRow = { section: t.section, param_index: parseInt(n, 10), source_key: 'guest_name' };
          if (t.section === 'button' && t.buttonIndex !== undefined) {
            r.button_index = t.buttonIndex;
            r.url_param_index = 0;
          }
          rows.push(r);
        }
      } else {
        const re = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g;
        let m: RegExpExecArray | null;
        const seen = new Set<string>();
        while ((m = re.exec(t.text)) !== null) {
          const name = m[1];
          const k = `${t.section}-${name}`;
          if (seen.has(k)) continue;
          seen.add(k);
          const r: VariableMappingRow = { section: t.section, name, source_key: 'guest_name' };
          if (t.section === 'button' && t.buttonIndex !== undefined) {
            r.button_index = t.buttonIndex;
            r.url_param_index = 0;
          }
          rows.push(r);
        }
      }
    }
    setVariableMapping(rows);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const bodyText = components.body?.text || '';
      const payload: Partial<WhatsAppTemplate> = {
        name: name.trim(),
        meta_template_name: (metaTemplateName || '').trim() || name.trim(),
        language_code: languageCode.trim() || 'en',
        template_category: templateCategory,
        parameter_format: parameterFormat,
        components,
        variable_mapping: variableMapping,
        content: bodyText,
        body_parameter_sources: variableMapping.filter((m) => m.section === 'body').map((m) => m.source_key),
      };
      if (id) {
        await whatsappTemplateService.updateWhatsAppTemplate(Number(id), payload);
      } else {
        await whatsappTemplateService.createWhatsAppTemplate(payload);
      }
      navigate('/whatsapp-templates');
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 960,
        margin: '0 auto',
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
          {id ? 'Edit template' : 'New WhatsApp template'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Build HEADER (text), BODY, FOOTER, and BUTTONS. On <strong>create</strong>, the template is submitted to Meta
          immediately. Editing an existing row updates local config only; changing Meta copy after approval is done in
          Meta.
        </Typography>
      </Box>

      {id && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Status: {status}. Use &quot;Sync all&quot; or row sync on the list page to refresh from Meta.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              required
              label="Display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              fullWidth
              label="Meta template name (optional)"
              value={metaTemplateName}
              onChange={(e) => setMetaTemplateName(e.target.value)}
              helperText="Falls back to display name. Lowercase+underscores recommended."
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="Language"
                value={languageCode}
                onChange={(e) => setLanguageCode(e.target.value)}
              />
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={templateCategory}
                  label="Category"
                  onChange={(e) => setTemplateCategory(e.target.value as typeof templateCategory)}
                >
                  <MenuItem value="UTILITY">UTILITY</MenuItem>
                  <MenuItem value="MARKETING">MARKETING</MenuItem>
                  <MenuItem value="AUTHENTICATION">AUTHENTICATION</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Parameter format</InputLabel>
                <Select
                  value={parameterFormat}
                  label="Parameter format"
                  onChange={(e) => setParameterFormat(e.target.value as 'POSITIONAL' | 'NAMED')}
                >
                  <MenuItem value="POSITIONAL">POSITIONAL ({"{{1}}, {{2}}"})</MenuItem>
                  <MenuItem value="NAMED">NAMED (e.g. {"{{guest_name}}"})</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Typography variant="h6">Components</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!components.header?.enabled}
                  onChange={(_, v) =>
                    setComponents((c: TemplateComponents) => ({ ...c, header: { ...c.header!, enabled: v } }))
                  }
                />
              }
              label="Header (TEXT)"
            />
            {components.header?.enabled && (
              <TextField
                fullWidth
                label="Header text"
                value={components.header.text}
                onChange={(e) =>
                  setComponents((c: TemplateComponents) => ({
                    ...c,
                    header: { ...c.header!, text: e.target.value, format: 'TEXT' },
                  }))
                }
              />
            )}

            <TextField
              fullWidth
              required
              multiline
              minRows={4}
              label="Body (required)"
              value={components.body?.text || ''}
              onChange={(e) => setComponents((c: TemplateComponents) => ({ ...c, body: { text: e.target.value } }))}
              helperText={
                parameterFormat === 'POSITIONAL' ? 'Use {{1}}, {{2}}, …' : 'Use {{name}} identifiers (no spaces inside {{ }})'
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={!!components.footer?.enabled}
                  onChange={(_, v) =>
                    setComponents((c: TemplateComponents) => ({ ...c, footer: { ...c.footer!, enabled: v } }))
                  }
                />
              }
              label="Footer (static, optional)"
            />
            {components.footer?.enabled && (
              <TextField
                fullWidth
                label="Footer text"
                value={components.footer.text}
                onChange={(e) =>
                  setComponents((c: TemplateComponents) => ({ ...c, footer: { ...c.footer!, text: e.target.value } }))
                }
              />
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1">Buttons (max 3)</Typography>
              <Button startIcon={<AddIcon />} onClick={addButton} disabled={(components.buttons || []).length >= 3}>
                Add button
              </Button>
            </Box>
            {(components.buttons || []).map((b: ButtonComponent, i: number) => (
              <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={b.type}
                      label="Type"
                      onChange={(e) =>
                        updateButton(i, 'type', e.target.value as ButtonComponent['type'])
                      }
                    >
                      <MenuItem value="QUICK_REPLY">QUICK_REPLY</MenuItem>
                      <MenuItem value="URL">URL</MenuItem>
                      <MenuItem value="PHONE_NUMBER">PHONE_NUMBER</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton onClick={() => removeButton(i)} size="small" aria-label="remove">
                    <DeleteIcon />
                  </IconButton>
                </Stack>
                <TextField
                  fullWidth
                  size="small"
                  label="Button text"
                  value={b.text || ''}
                  onChange={(e) => updateButton(i, 'text', e.target.value)}
                  sx={{ mb: 1 }}
                />
                {b.type === 'URL' && (
                  <TextField
                    fullWidth
                    size="small"
                    label="URL (may include {{1}} or named vars)"
                    value={b.url || ''}
                    onChange={(e) => updateButton(i, 'url', e.target.value)}
                  />
                )}
                {b.type === 'PHONE_NUMBER' && (
                  <TextField
                    fullWidth
                    size="small"
                    label="E.164 phone"
                    value={b.phone_number || ''}
                    onChange={(e) => updateButton(i, 'phone_number', e.target.value)}
                  />
                )}
              </Paper>
            ))}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={autofillMapping} type="button">
                Autofill variable mapping
              </Button>
            </Box>

            <Typography variant="subtitle1">Variable mapping (reservation context)</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Section</TableCell>
                  {parameterFormat === 'POSITIONAL' ? <TableCell>Param #</TableCell> : <TableCell>Name</TableCell>}
                  <TableCell>Context field</TableCell>
                  <TableCell>Button idx</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variableMapping.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Select
                        size="small"
                        value={row.section}
                        onChange={(e) => {
                          const n = [...variableMapping];
                          n[idx] = { ...n[idx], section: e.target.value as VariableMappingRow['section'] };
                          setVariableMapping(n);
                        }}
                      >
                        <MenuItem value="header">header</MenuItem>
                        <MenuItem value="body">body</MenuItem>
                        <MenuItem value="footer">footer</MenuItem>
                        <MenuItem value="button">button</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {parameterFormat === 'POSITIONAL' ? (
                        <TextField
                          size="small"
                          type="number"
                          value={row.param_index ?? ''}
                          onChange={(e) => {
                            const n = [...variableMapping];
                            n[idx] = { ...n[idx], param_index: parseInt(e.target.value, 10) || 0 };
                            setVariableMapping(n);
                          }}
                        />
                      ) : (
                        <TextField
                          size="small"
                          value={row.name || ''}
                          onChange={(e) => {
                            const n = [...variableMapping];
                            n[idx] = { ...n[idx], name: e.target.value };
                            setVariableMapping(n);
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={row.source_key}
                        onChange={(e) => {
                          const n = [...variableMapping];
                          n[idx] = { ...n[idx], source_key: e.target.value };
                          setVariableMapping(n);
                        }}
                      >
                        {SOURCE_KEYS.map((k) => (
                          <MenuItem key={k} value={k}>
                            {k}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        placeholder="btn"
                        value={row.button_index ?? ''}
                        onChange={(e) => {
                          const n = [...variableMapping];
                          n[idx] = { ...n[idx], button_index: parseInt(e.target.value, 10) || 0 };
                          setVariableMapping(n);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setVariableMapping((m) => m.filter((_, j) => j !== idx))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button
              type="button"
              onClick={() =>
                setVariableMapping((m) => [
                  ...m,
                  parameterFormat === 'POSITIONAL'
                    ? { section: 'body', param_index: 1, source_key: 'guest_name' }
                    : { section: 'body', name: 'guest_name', source_key: 'guest_name' },
                ])
              }
            >
              Add mapping row
            </Button>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
              <Button variant="outlined" onClick={() => navigate('/whatsapp-templates')}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={loading || !name.trim()}>
                {loading ? 'Saving...' : id ? 'Update local template' : 'Create on Meta'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default WhatsAppTemplateEditor;
